import { useState } from 'react'
import {
  Alert,
  Badge,
  Container,
  Group,
  SimpleGrid,
  Stack,
  Text,
  UnstyledButton,
  Center,
  Loader,
} from '@mantine/core'
import {
  IconShieldCheck,
  IconFingerprint,
  IconRoute,
  IconSearch,
  IconArrowRight,
  IconPackage,
  IconUsersPlus,
} from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useSearchDashboard } from '@/hooks/useAnalytics'
import { WorkloadBreakdownCard } from '@/components/dashboard/WorkloadBreakdownCard'
import { MetricCard, PageHeader, FilterBar, ChartCard, getPresetRange } from '@/components/shared'
import type { SearchQueueBreakdownItem } from '@/types'
import { usePermissions } from '@/hooks/usePermissions'
import { useVersionContext } from '@/context/VersionContext'

const fmt = (v: number | null | undefined) => (v ?? 0).toLocaleString()

const SEARCH_QUEUE_COLORS: Record<string, string> = {
  'Insufficient Data': 'orange',
  Auditor: 'blue',
  Exclusion: 'gray',
  Automation: 'teal',
  'Ready for QA': 'grape',
}

const quickNavItems = [
  { path: '/search', label: 'Search Analytics', icon: IconSearch, description: 'Search-level table and drill-down' },
  { path: '/compliance', label: 'Compliance', icon: IconShieldCheck, description: 'Offense-level reportability analysis' },
  { path: '/identity', label: 'Identity Verification', icon: IconFingerprint, description: 'Match scores and confidence levels' },
  { path: '/routing', label: 'Decision Routing', icon: IconRoute, description: 'Queue distribution and routing matrix' },
  { path: '/automation', label: 'Automation', icon: IconPackage, description: 'Product automation breakdown' },
]

const operationsNavItems = [
  { path: '/ops/client-onboarding', label: 'Client Onboarding', icon: IconUsersPlus, description: 'Add new tenants and track onboarding state' },
]

function SearchQueueCards({ items }: { items: SearchQueueBreakdownItem[] }) {
  const visible = items.filter(i => i.searchQueue !== 'Other / Unknown' && i.searchCount > 0)
  if (visible.length === 0) return null

  return (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
      {visible.map(item => (
        <Group key={item.searchQueue} justify="space-between" p="sm" style={{ border: '1px solid var(--mantine-color-gray-2)', borderRadius: 8 }}>
          <div>
            <Badge variant="light" color={SEARCH_QUEUE_COLORS[item.searchQueue] || 'gray'} size="sm" mb={4}>
              {item.searchQueue}
            </Badge>
            <Text size="xs" c="dimmed">{item.pct.toFixed(1)}% of searches</Text>
          </div>
          <Text size="lg" fw={700}>{fmt(item.searchCount)}</Text>
        </Group>
      ))}
    </SimpleGrid>
  )
}

export function OverviewDashboard() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { canManageSettings } = usePermissions()
  const [preset, setPreset] = useState('30d')
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(getPresetRange('30d'))

  const handlePresetChange = (value: string) => {
    setPreset(value)
    if (value !== 'custom') setDateRange(getPresetRange(value))
  }

  const handleDateRangeChange = (value: [Date | null, Date | null]) => {
    setDateRange(value)
    setPreset('custom')
  }

  const startDate = dateRange[0]?.toISOString().split('T')[0] || ''
  const endDate = dateRange[1]?.toISOString().split('T')[0] || ''

  const { selectedVersion } = useVersionContext()

  const { data: dashboard, isLoading, refetch, isFetching } = useSearchDashboard({
    startDate,
    endDate,
    page: 1,
    pageSize: 1,
    includeMetrics: true,
    modelVersion: selectedVersion || undefined,
  })

  const metrics = dashboard?.metrics || null
  const hasSearches = (metrics?.totalSearches ?? 0) > 0
  const navigationItems = canManageSettings
    ? [...quickNavItems, ...operationsNavItems]
    : quickNavItems

  const handleRefresh = () => {
    refetch()
    queryClient.invalidateQueries({ queryKey: ['routing-metrics', startDate, endDate] })
  }

  if (isLoading && !dashboard) {
    return (
      <Center h={400}>
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed" size="sm">Loading overview...</Text>
        </Stack>
      </Center>
    )
  }

  return (
    <Container fluid>
      <Stack gap="lg">
        <PageHeader
          title="Overview"
          subtitle="Offense-level routing outcomes and search volume"
          onRefresh={handleRefresh}
          isLoading={isFetching}
        />

        <FilterBar
          preset={preset}
          onPresetChange={handlePresetChange}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
        />

        {hasSearches && (
          <MetricCard
            label="Total Searches"
            value={fmt(metrics!.totalSearches)}
            subtitle={`${fmt(metrics!.totalOffenses)} offenses in selected period`}
            icon={<IconSearch size={18} />}
          />
        )}

        <WorkloadBreakdownCard startDate={startDate} endDate={endDate} />

        {metrics && hasSearches && (metrics.searchQueueBreakdown?.length ?? 0) > 0 && (
          <ChartCard
            title="Search Queue Distribution"
            subtitle="Search-level routing — Automation, Auditor, and Insufficient Data"
          >
            <SearchQueueCards items={metrics.searchQueueBreakdown} />
          </ChartCard>
        )}

        {!hasSearches && (
          <Alert variant="light" color="gray" title="No searches in this date range">
            Try a wider range such as the last 30 days, or pick dates when data was processed.
          </Alert>
        )}

        {hasSearches && (
          <Group justify="flex-end">
            <UnstyledButton onClick={() => navigate('/search')}>
              <Group gap={4}>
                <Text size="sm" c="brand" fw={500}>View all searches</Text>
                <IconArrowRight size={14} color="var(--mantine-color-brand-6)" />
              </Group>
            </UnstyledButton>
          </Group>
        )}

        <div>
          <Group justify="space-between" mb="sm">
            <Text fw={600} size="sm">Explore</Text>
            <Text size="xs" c="dimmed">
              {canManageSettings ? 'Open analytics and operations workspaces' : 'Drill into detailed analytics'}
            </Text>
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 5 }}>
            {navigationItems.map((item) => {
              const Icon = item.icon
              return (
                <UnstyledButton
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  style={{
                    border: '1px solid var(--mantine-color-gray-2)',
                    borderRadius: 'var(--mantine-radius-md)',
                    padding: 16,
                    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--mantine-color-brand-3)'
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--mantine-color-gray-2)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                >
                  <Group gap="sm" mb="xs">
                    <Icon size={20} stroke={1.5} color="var(--mantine-color-brand-6)" />
                    <Text size="sm" fw={600}>{item.label}</Text>
                  </Group>
                  <Text size="xs" c="dimmed" lineClamp={2}>{item.description}</Text>
                  <Group gap={4} mt="xs">
                    <Text size="xs" c="brand" fw={500}>Open</Text>
                    <IconArrowRight size={12} color="var(--mantine-color-brand-6)" />
                  </Group>
                </UnstyledButton>
              )
            })}
          </SimpleGrid>
        </div>
      </Stack>
    </Container>
  )
}
